/**
 * Step gradient for MeshToonMaterial. The brightness ramp + step count
 * controls how "hand-drawn" planets read.
 *
 * Default 4 steps (deep shadow → midshadow → midlight → bright) with a
 * wide brightness range so cel bands clearly separate. Brightness floor
 * stays above pure black so the shadow side remains readable on dark
 * backgrounds.
 */

import { DataTexture, NearestFilter, RedFormat, RepeatWrapping } from "three";

export type ToonSteps = 2 | 3 | 4 | 5;

const SHADOW_FLOOR = 70;
const HIGHLIGHT_CEIL = 255;

// Hand-tuned per step count — pure step transitions (no easing) so each
// cel band is a flat color region with a sharp edge between them.
// Values pushed toward the bright end so most of the planet face reads
// as the lit color, with a clear shadow band near the terminator.
const STEP_TABLE: Record<ToonSteps, ReadonlyArray<number>> = {
  2: [SHADOW_FLOOR, HIGHLIGHT_CEIL],
  3: [SHADOW_FLOOR, 168, HIGHLIGHT_CEIL],
  4: [SHADOW_FLOOR, 140, 210, HIGHLIGHT_CEIL],
  5: [SHADOW_FLOOR, 120, 180, 225, HIGHLIGHT_CEIL],
};

export function createToonGradient(steps: ToonSteps = 4): DataTexture {
  const values = STEP_TABLE[steps];
  const data = new Uint8Array(steps);
  for (let i = 0; i < steps; i += 1) {
    data[i] = values[i]!;
  }
  const tex = new DataTexture(data, steps, 1, RedFormat);
  tex.minFilter = NearestFilter;
  tex.magFilter = NearestFilter;
  tex.wrapS = RepeatWrapping;
  tex.needsUpdate = true;
  return tex;
}

export const __testing = { SHADOW_FLOOR, HIGHLIGHT_CEIL, STEP_TABLE };
